<template>
  <div class="view-container reference-profile-users-page">
    <div class="extended-filter-block">
      <div class="back-button-wrapper" @click="goBack('/references/list')">
        <LeftOutlined class="back-button" />
      </div>

      <h2>Выборка: {{ referenceProfile?.title ?? 'Эталонный профиль' }}</h2>
    </div>

    <div class="card card__with-action">
      <div class="reference-profile-users-page__top">
        <div class="reference-profile-users-page__tabs">
          <a-segmented
              v-model:value="currentStatus"
              :options="statusOptions"
          />
        </div>

        <div class="action-button-wrapper">
          <a-button
              class="primary-button"
              @click="handleAddUser"
          >
            <template #icon>
              <PlusOutlined />
            </template>

            Добавить
          </a-button>
        </div>
      </div>

      <TableHorizontalScrollShell ref="tableShell">
        <a-table
            ref="tableRef"
            row-key="id"
            class="reference-profile-users-table"
            :columns="columns"
            :data-source="users"
            :scroll="tableScroll"
            :row-selection="{ selectedRowKeys: selectedUsers, onChange: onSelectChange, columnWidth: 40 }"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            @change="handleTableChange"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'name'">
              <a class="staff-link" @click="router.push(`/staff/${record.id}/edit`)">{{ text }}</a>
            </template>

            <template v-if="column.dataIndex === 'status'">
              <a-tag :class="['status-tag', tagColorStatus[record.status]]">
                {{ getStatusLabel(record.status) }}
              </a-tag>
            </template>

            <template v-if="column.dataIndex === 'settings'">
              <a-dropdown :trigger="['click']" placement="bottomLeft">
                <a-button type="text" @click.prevent>
                  <MenuOutlined />
                </a-button>

                <template #overlay>
                  <a-menu @click="({ key }) => handleAction(key, record)">
                    <a-menu-item
                        v-for="action in actions"
                        :key="action.id"
                    >
                      {{ action.name }}
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img
                  :src="NotFoundIcon"
                  class="no-data-image"
                  alt="Нет данных"
              />

              <div class="no-data-text">
                {{
                  currentStatus !== 'all'
                      ? 'Нет данных для отображения. Измените вкладку.'
                      : 'Нет данных для отображения.'
                }}
              </div>

              <a-button
                  v-if="currentStatus !== 'all'"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>

                Сбросить фильтры
              </a-button>

              <a-button
                  v-else
                  @click="fetchUsers"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>

                Обновить
              </a-button>
            </div>
          </template>
        </a-table>
      </TableHorizontalScrollShell>

      <div class="bottom-container">
        <div class="action-block reference-profile-users-page__actions" v-if="!isMobile || isMobile && selectedUsers.length">
          <div class="action-select-wrapper">
            <a-select
                v-model:value="actionToApply"
                placeholder="Выберите действие"
                style="width: 100%;"
                :not-found-content="'Нет данных'"
                :disabled="!selectedUsers.length"
                @change="() => handleAction(actionToApply)"
            >
              <a-select-option
                  v-for="action in groupActions"
                  :key="action.id"
                  :value="action.id"
              >
                {{ action.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <a-pagination
            v-model:current="currentPage"
            :page-size="entitiesPerPage"
            :buildOptionText="count => `${count.value} / стр`"
            :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
            :total="totalUsers"
            :show-less-items="isMobile"
            @change="handlePageChange"
        />
      </div>
    </div>

    <ReferenceProfileEditForm
      :open="showAddForm"
      :profileId="referenceProfile?.id ?? null"
      :profileTitle="referenceProfile?.title ?? ''"
      @close="hideAddForm"
      @referenceUpdated="resetFilters"
    />

  </div>
</template>

<script>
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import {onBeforeRouteLeave, useRoute, useRouter} from 'vue-router'
import { useStore } from 'vuex'
import { message } from 'ant-design-vue'
import {
  LeftOutlined,
  MenuOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons-vue'

import NotFoundIcon from '@/assets/images/icons/no_data.svg'
import TableHorizontalScrollShell from '@/components/Tables/TableScrollComponent.vue'
import { useTableColumns } from '@/mixins/useTableColumns'
import ReferenceProfileEditForm from "@/feature/references/ReferenceProfileEditForm.vue";

export default {
  name: 'reference-profile-users-table',

  components: {
    ReferenceProfileEditForm,
    TableHorizontalScrollShell,

    LeftOutlined,
    MenuOutlined,
    MoreOutlined,
    PlusOutlined,
    ReloadOutlined,
  },

  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute();

    const tableShell = ref(null)
    const tableLoading = ref(false)

    const currentStatus = ref('all')
    const selectedUsers = ref([])
    const actionToApply = ref(null)

    const currentPage = ref(1)
    const entitiesPerPage = ref(10)

    const showAddForm = ref(false);

    const sortState = ref({
      field: null,
      order: null,
    });

    const stateDetailList = computed(() => store.state.references?.stateDetailList ?? {})

    const referenceProfile = computed(() => {
      return store.state.references?.referenceProfile ?? null;
    });

    const tagColorStatus = {
      in_profile: 'ant-tag-green',
      no_passing: 'yellow-tag',
      anomaly: 'red-tag',
    }

    const statusOptions = [
      {
        label: 'Все',
        value: 'all',
      },
      {
        label: 'В профиле',
        value: 'in_profile',
      },
      {
        label: 'Без оценки',
        value: 'no_passing',
      },
      {
        label: 'Аномалии',
        value: 'anomaly',
      },
    ]

    const statusLabels = {
      in_profile: 'В профиле',
      no_passing: 'Без оценки',
      anomaly: 'Аномалия',
    }

    const initialColumns = ref([
      {
        title: 'ФИО',
        dataIndex: 'name',
        sorter: true,
        visible: true,
        width: 400,
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        sorter: true,
        visible: true,
        width: 250
      },
      {
        title: 'Должность',
        dataIndex: 'job_title',
        sorter: true,
        visible: true,
        width: 200
      },
      {
        title: 'Дата добавления',
        dataIndex: 'updated_at',
        sorter: true,
        visible: true,
        width: 200
      },
    ])

    const { columns, scrollX, scrollY, allColumns, tableRef, tableScroll, updateTableYScroll } = useTableColumns(
        initialColumns,
        {
          tableKey: 'reference_profile_users',
          settingsWidth: 40,
          selectionWidth: 40,
        },
    )

    const actions = [
      {
        id: 'delete',
        name: 'Удалить',
      },
      {
        id: 'remind_score',
        name: 'Напомнить об оценке',
      },
    ]

    const groupActions = [
      {
        id: 'delete',
        name: 'Удалить',
      },
      {
        id: 'remind_score',
        name: 'Напомнить об оценке',
      },
    ]

    const usersData = computed(() => {
      return store.state.references?.detailReportUsers?.data ?? []
    })

    const totalUsers = computed(() => {
      return store.state.references?.detailReportUsers?.total ?? 0
    })

    const users = computed(() => {
      return (usersData.value ?? []).map((item) => {
        return {
          ...item,
          name: item.name ?? '',
          status: item.status ?? '',
          job_title: item.job_title ?? '',
          updated_at: item.updated_at_formatted ?? '',
        }
      })
    })

    const requestParams = computed(() => {
      return {
        id: route.params.id,
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        status: currentStatus.value === 'all' ? null : currentStatus.value,
      }
    })

    const getStatusLabel = (status) => {
      return statusLabels[status] ?? status
    }

    const fetchUsers = () => {
      tableLoading.value = true

      return store.dispatch('references/getReportUsers', requestParams.value)
          .finally(() => {
            tableLoading.value = false
          })
    }

    const handleAddUser = () => {
      showAddForm.value = true;
    }

    const handleAction = (action, record = null) => {
      if (!action) {
        return
      }

      if (!record && !selectedUsers.value.length) {
        message.error('Выберите сотрудников')
        actionToApply.value = null
        return
      }

      const userIds = record?.id ? [record.id] : selectedUsers.value

      if (action === 'delete') {
        deleteUsers(userIds)
        return
      }

      if (action === 'remind_score') {
        remindScore(userIds)
      }
    }

    const deleteUsers = (userIds) => {
      message.info('Идет удаление');

      return store.dispatch('references/deleteUsersFromReport', {
        users: userIds,
        id: route.params.id,
      }).then(() => {
        clearCurrentAction();
        message.info('Пользователи удалены');
        fetchAfterSubmit();
      })
    }

    const remindScore = (userIds) => {
      return store.dispatch('test/remindAboutPassings', {
        users: userIds,
      }).then(() => {
        message.success('Уведомления направлены');
        clearCurrentAction();
      })
    }

    const onSelectChange = (selectedRowKeys) => {
      selectedUsers.value = selectedRowKeys
    }

    const resetFilters = () => {
      currentStatus.value = 'all'
      currentPage.value = 1
      selectedUsers.value = []
      actionToApply.value = null
      fetchUsers()
    }

    const hideAddForm = () => {
      showAddForm.value = false;
    }

    const handleTableChange = (pagination, filters, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      }

      currentPage.value = 1
      selectedUsers.value = []

      fetchUsers()
    }

    const retrieveEntitiesPerPage = () => {
      const storageValue = localStorage.getItem('perPage')
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10
    }

    const restoreListState = () => {
      if (!stateDetailList.value || !Object.keys(stateDetailList.value).length) {
        return
      }

      currentPage.value = stateDetailList.value.currentPage ?? currentPage.value
      entitiesPerPage.value = stateDetailList.value.entitiesPerPage ?? entitiesPerPage.value
      currentStatus.value = stateDetailList.value.status ?? currentStatus.value
      sortState.value = {
        field: stateDetailList.value.sortField ?? null,
        order: stateDetailList.value.sortOrder ?? null,
      }
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage
        setEntitiesPerPage(entitiesPerPage.value)
      }

      currentPage.value = current
      selectedUsers.value = []

      fetchUsers()
    }

    const clearSelection = () => {
      selectedUsers.value = []
      actionToApply.value = null
    }


    const clearCurrentAction = () => {
      actionToApply.value = null
    }

    const fetchAfterSubmit = () => {
      clearSelection()
      fetchUsers()
    }

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back()
      } else {
        router.push(url)
      }
    }

    onBeforeMount(() => {
      store.dispatch('references/getReferenceProfile', {id: route.params.id});
      retrieveEntitiesPerPage()
      restoreListState()
      fetchUsers();
    })

    onBeforeRouteLeave((to) => {
      const isStaffRoute = to.path.startsWith('/staff')

      if (!isStaffRoute) {
        store.commit('references/clearStateDetailList')
      }
    })

    watch(currentStatus, () => {
      currentPage.value = 1
      selectedUsers.value = []
      actionToApply.value = null

      fetchUsers()
    })

    watch(tableLoading, async (loading) => {
      if (!loading) {
        await tableShell.value?.rebindScroll()
        await updateTableYScroll()
      }
    })

    watch(users, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    watch(allColumns, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    watch(
        [currentPage, entitiesPerPage, sortState, currentStatus],
        () => {
          store.commit('references/setStateDetailList', {
            currentPage: currentPage.value,
            entitiesPerPage: entitiesPerPage.value,
            sortField: sortState.value.field,
            sortOrder: sortState.value.order,
            status: currentStatus.value,
          })
        },
        { deep: true },
    )

    return {
      router,
      NotFoundIcon,

      tableShell,
      tableRef,
      tableScroll,
      tableLoading,

      currentStatus,
      statusOptions,
      referenceProfile,

      columns,
      scrollX,
      scrollY,

      users,
      totalUsers,

      selectedUsers,
      actionToApply,
      actions,
      groupActions,

      currentPage,
      entitiesPerPage,

      getStatusLabel,
      fetchUsers,
      handleAddUser,
      handleAction,
      onSelectChange,
      resetFilters,
      handleTableChange,
      handlePageChange,
      goBack,
      showAddForm,

      tagColorStatus,
      hideAddForm
    }
  },
}
</script>

<style scoped lang="scss">
.reference-profile-users-page {
  padding-top: 0;
  padding-bottom: 0;
}

.card__with-action {
  margin-bottom: 0;
}

.reference-profile-users-page__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
    gap: 8px;
  }
}

.reference-profile-users-page__tabs {
  flex-shrink: 0;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;
  }
}

.reference-profile-users-page__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.action-select-wrapper {
  min-width: 180px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
}

.action-button-wrapper {
  display: flex;
  column-gap: 8px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    button {
      width: 100%;
    }
  }
}

.staff-link {
  cursor: pointer;
  color: var(--main-green);
}

.staff-link:hover {
  color: var(--hover-green);
}

.status-tag {
  margin: 0;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
}

.no-data-wrapper {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
}

.no-data-text {
  color: var(--main-font);
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

:deep(.ant-btn) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  padding: 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr:not(.ant-table-measure-row) > td) {
  padding: 10px 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
  height: auto !important;
  padding: 0 !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
  color: var(--main-font);
}

:deep(.anticon-reload) {
  cursor: pointer;
}

:deep(.anticon-reload:hover) {
  color: var(--main-green) !important;
}

:deep(.ant-segmented) {
  background: #f5f5f5;
}

:deep(.reference-profile-users-table .ant-table-tbody>tr:not(.ant-table-measure-row) .ant-checkbox-wrapper) {
  height: 18px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-dropdown-trigger)),
:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-checkbox-wrapper)) {
  padding: 10px 5px !important;
}

//:deep(.reference-profile-users-table .ant-table-header table colgroup col:nth-child(1)),
//:deep(.reference-profile-users-table .ant-table-body table colgroup col:nth-child(1)) {
//  width: 30px !important;
//  min-width: 30px !important;
//  max-width: 30px !important;
//}
//
//:deep(.reference-profile-users-table .ant-table-header table colgroup col:nth-child(2)),
//:deep(.reference-profile-users-table .ant-table-body table colgroup col:nth-child(2)) {
//  width: 35px !important;
//  min-width: 35px !important;
//  max-width: 35px !important;
//}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #ffffff;
  border-radius: 25%;
  cursor: pointer;
}

@media (max-width: $mobile-breakpoint) {
  :deep(.ant-pagination .ant-pagination-total-text) {
    display: none !important;
  }

  .reference-profile-users-page__actions {
    width: 100%;
  }
}

:deep(.ant-segmented) {
  font-family: "Open Sans", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

:deep(.reference-profile-users-table .ant-table-header) {
  scrollbar-gutter: stable;
}

:deep(.reference-profile-users-table .ant-table-body) {
  scrollbar-gutter: stable;
}
</style>
